import React from "react";
import { connect } from "react-redux";
import { meProducersSelector } from "../../helpers/Selectors";
import { withRoomContext } from "../../context/RoomContext";
import { withStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import * as appPropTypes from "../../typings/appPropTypes";
import { useIntl } from "react-intl";
import MicIcon from "@material-ui/icons/MicNone";
import MicOffIcon from "@material-ui/icons/MicOffOutlined";
import VideoIcon from "@material-ui/icons/VideocamOutlined";
import VideoOffIcon from "@material-ui/icons/VideocamOffOutlined";
import ScreenIcon from "@material-ui/icons/ScreenShareOutlined";
import ScreenOffIcon from "@material-ui/icons/StopScreenShareOutlined";
import PanTool from "@material-ui/icons/PanToolOutlined";
import CallEnd from "@material-ui/icons/CallEndOutlined";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import * as roomActions from "../../actions/roomActions";

const styles = (theme) => ({
  root: {
    display: "flex",
    flexDirection: "row",
    alignSelf: "center",
    textAlign: "center",
    justifyContent: "center",
    flexGrow: 1,
  },
  leaveButton: {
    backgroundColor: "#f3465a",
    "&:hover": {
      backgroundColor: "#f3465a",
    },
  },
  actionButton: {
    marginRight: theme.spacing(2),
    border: "solid 1px #eaedf0",
  },
  actionButtonPassive: {
    marginRight: theme.spacing(2),
    backgroundColor: "#f7f8fd",
    "&:hover": {
      backgroundColor: "#f7f8fd",
    },
    border: "solid 1px #eaedf0",
  },
  screenShareButton: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  buttonProgress: {
    color: "black",
  },
});
const BottomControlBar = (props) => {
  const intl = useIntl();

  const {
    roomClient,
    me,
    micProducer,
    webcamProducer,
    screenProducer,
    canShareScreen,
    setLeaveDialogOpen,
    webinar,
    currentCall,
    handleRoomClose,
    classes,
    hasWebinarPermission,
  } = props;

  let micState;

  let micTip;

  if (!me.canSendMic) {
    micState = "unsupported";
    micTip = intl.formatMessage({
      id: "device.audioUnsupported",
      defaultMessage: "Audio unsupported",
    });
  } else if (!micProducer) {
    micState = "off";
    micTip = intl.formatMessage({
      id: "device.activateAudio",
      defaultMessage: "Activate audio",
    });
  } else if (!micProducer.locallyPaused && !micProducer.remotelyPaused) {
    micState = "on";
    micTip = intl.formatMessage({
      id: "device.muteAudio",
      defaultMessage: "Mute audio",
    });
  } else {
    micState = "muted";
    micTip = intl.formatMessage({
      id: "device.unMuteAudio",
      defaultMessage: "Unmute audio",
    });
  }

  let webcamState;

  let webcamTip;

  if (!me.canSendWebcam) {
    webcamState = "unsupported";
    webcamTip = intl.formatMessage({
      id: "device.videoUnsupported",
      defaultMessage: "Video unsupported",
    });
  } else if (webcamProducer) {
    webcamState = "on";
    webcamTip = intl.formatMessage({
      id: "device.stopVideo",
      defaultMessage: "Stop video",
    });
  } else {
    webcamState = "off";
    webcamTip = intl.formatMessage({
      id: "device.startVideo",
      defaultMessage: "Start video",
    });
  }

  let screenState;

  let screenTip;

  if (!me.canShareScreen) {
    screenState = "unsupported";
    screenTip = intl.formatMessage({
      id: "device.screenSharingUnsupported",
      defaultMessage: "Screen sharing not supported",
    });
  } else if (screenProducer) {
    screenState = "on";
    screenTip = intl.formatMessage({
      id: "device.stopScreenSharing",
      defaultMessage: "Screen sharing",
    });
  } else {
    screenState = "off";
    screenTip = intl.formatMessage({
      id: "device.startScreenSharing",
      defaultMessage: "Screen sharing",
    });
  }

  return (
    <div className={classes.root}>
      {(!webinar || hasWebinarPermission) && me.canSendMic && (
        <Tooltip placement="left" title={micTip}>
          <span>
            <IconButton
              disabled={me.audioInProgress}
              onClick={() => {
                if (micState === "off") roomClient.enableMic();
                else if (micState === "on") roomClient.muteMic();
                else roomClient.unmuteMic();
              }}
              className={
                micState === "on"
                  ? classes.actionButton
                  : classes.actionButtonPassive
              }
              color="primary"
            >
              {me.audioInProgress ? (
                <CircularProgress
                  size={24}
                  className={classes.buttonProgress}
                />
              ) : micState === "on" ? (
                <MicIcon style={{ color: "#181c1e" }} />
              ) : (
                <MicOffIcon style={{ color: "#181c1e" }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}

      {(!webinar || hasWebinarPermission) && me.canSendWebcam && (
        <Tooltip placement="right" title={webcamTip}>
          <span>
            <IconButton
              disabled={me.webcamInProgress}
              onClick={() => {
                webcamState === "on"
                  ? roomClient.disableWebcam()
                  : roomClient.enableWebcam();
              }}
              className={
                webcamState === "on"
                  ? classes.actionButton
                  : classes.actionButtonPassive
              }
              color="primary"
            >
              {me.webcamInProgress ? (
                <CircularProgress
                  size={24}
                  className={classes.buttonProgress}
                />
              ) : webcamState === "on" ? (
                <VideoIcon style={{ color: "#181c1e" }} />
              ) : (
                <VideoOffIcon style={{ color: "#181c1e" }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}

      {(!webinar || hasWebinarPermission) &&
        canShareScreen &&
        me.canShareScreen && (
          <Tooltip
            className={classes.screenShareButton}
            placement="right"
            title={screenTip}
          >
            <IconButton
              onClick={() => {
                switch (screenState) {
                  case "on": {
                    roomClient.disableScreenSharing();
                    break;
                  }
                  case "off": {
                    roomClient.enableScreenSharing();
                    break;
                  }
                  default: {
                    break;
                  }
                }
              }}
              className={
                screenState === "on"
                  ? classes.actionButton
                  : classes.actionButtonPassive
              }
              color="primary"
            >
              {screenState === "on" || screenState === "unsupported" ? (
                <ScreenIcon style={{ color: "#181c1e" }} />
              ) : (
                <ScreenOffIcon style={{ color: "#181c1e" }} />
              )}
            </IconButton>
          </Tooltip>
        )}

      {webinar && !hasWebinarPermission && (
        <Tooltip
          placement="left"
          title={
            !me.raiseHand
              ? intl.formatMessage({
                  id: "room.raiseHand",
                  defaultMessage: "Raise Hand",
                })
              : intl.formatMessage({
                  id: "label.cancel",
                  defaultMessage: "Cancel",
                })
          }
        >
          <IconButton
            onClick={() => {
              me.raiseHand
                ? roomClient.sendRaiseHandState(false)
                : roomClient.sendRaiseHandState(true);
            }}
            className={
              me.raiseHand ? classes.actionButton : classes.actionButtonPassive
            }
            color="primary"
          >
            {me.raiseHand ? (
              <PanTool style={{ color: "#3289ef" }} />
            ) : (
              <PanTool style={{ color: "#181c1e" }} />
            )}
          </IconButton>
        </Tooltip>
      )}
      <Tooltip
        placement="right"
        disableFocusListener
        disableTouchListener
        title={intl.formatMessage({
          id: "label.leave",
          defaultMessage: "Leave",
        })}
      >
        <span>
          <IconButton
            className={classes.leaveButton}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Arama dahil her durumda onay popup'ı açılsın
              setLeaveDialogOpen(true);
            }}
          >
            <CallEnd style={{ color: "#fff" }} />
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
};

BottomControlBar.propTypes = {
  roomClient: PropTypes.any.isRequired,
  me: appPropTypes.Me.isRequired,
  settings: PropTypes.object,
  activeSpeaker: PropTypes.bool,
  micProducer: appPropTypes.Producer,
  webcamProducer: appPropTypes.Producer,
  screenProducer: appPropTypes.Producer,
  spacing: PropTypes.number,
  style: PropTypes.object,
  smallButtons: PropTypes.bool,
  webinar: PropTypes.bool,
  canShareScreen: PropTypes.bool.isRequired,
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  isModerator: PropTypes.bool,
};

const mapStateToProps = (state) => {
  return {
    me: state.me,
    ...meProducersSelector(state),
    settings: state.settings,
    activeSpeaker: state.me.id === state.room.activeSpeakerId,
    canShareScreen: state.me.roles.some((role) =>
      state.room.permissionsFromRoles.SHARE_SCREEN.includes(role)
    ),
    hasWebinarPermission:
      state.me.roles.some((role) =>
        state.room.permissionsFromRoles.MODERATE_WEBINAR.includes(role)
      ) ||
      state.me.roles.some((role) =>
        state.room.permissionsFromRoles.WEBINAR_SPEAKER.includes(role)
      ),
    webinar: state.room.webinar,
    isModerator: state.me.roles.some((role) =>
      state.room.permissionsFromRoles.MODERATE_ROOM.includes(role)
    ),
  };
};

const mapDispatchToProps = (dispatch) => ({
  setLeaveDialogOpen: (leaveDialogOpen) => {
    dispatch(roomActions.setLeaveDialogOpen({ leaveDialogOpen }));
  },
});

export default withRoomContext(
  connect(mapStateToProps, mapDispatchToProps, null, {
    areStatesEqual: (next, prev) => {
      return (
        prev.room.permissionsFromRoles === next.room.permissionsFromRoles &&
        prev.me === next.me &&
        prev.me.raiseHand === next.me.raiseHand &&
        prev.producers === next.producers &&
        prev.settings === next.settings &&
        prev.room.activeSpeakerId === next.room.activeSpeakerId &&
        prev.room.webinar === next.room.webinar
      );
    },
  })(withStyles(styles, { withTheme: true })(BottomControlBar))
);
